<template>
  <div
    v-if="props.noTip"
    class="collect-container"
    :class="[
      props.class,
      disabled ? 'collect-container--disabled' : 'collect-container--clickable',
    ]"
    @click.stop.prevent="onClick"
  >
    <slot :like="like">
      <component :is="icon" :like="like" :size="size" :iconClass="iconClass"></component>
    </slot>
  </div>
  <Tooltip v-else :content="displayText" trigger="hover" :enabled="!disabled">
    <div
      class="collect-container"
      :class="[
        props.class,
        disabled ? 'collect-container--disabled' : 'collect-container--clickable',
      ]"
      @click.stop.prevent="onClick"
    >
      <slot :like="like">
        <component :is="icon" :like="like" :size="size" :iconClass="iconClass"></component>
      </slot>
    </div>
  </Tooltip>
</template>

<script lang="ts" setup>
import { addCollect, cancelCollect } from '@/api';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { h } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BrandItem, Template } from '~/api/interface';
import type { Character } from '~/api/interface/character';
import type { IMediaCard } from '~/components/listCard';
import { COLLECTION_SOURCE } from '~/utils/constant';
import { getTagIDs } from '../template';
import BtnIcon from './btn.vue';
import DefaultIcon from './default.vue';
import ShadowIcon from './shadow.vue';
const { t: $t } = useI18n();

interface Params {
  noTip?: boolean; // 是否不显示tip
  is_user_upload?: boolean; // 是否是用户上传的收藏
  model_id: number; // 对应内容id，如作品id，成片id,素材id，片头片尾id
  model_type?: COLLECTION_MAP; // 收藏类型 COLLECTION_MAP
  source?: COLLECTION_SOURCE;
  resource_type?: RESOURCE_TYPE; // 资源类型 TARGET_FROM
  media_type?: FILE_TYPE_MAP; // 素材类型 FILE_TYPE_MAP
  [key: string | number]: any;
}

const props = withDefaults(
  defineProps<{
    noTip?: boolean;
    modelValue?: boolean;
    disabled?: boolean;
    params: Params;
    size?: number;
    disabledText?: string;
    data?: BrandItem | Template | IMediaCard | Character;
    iconClass?: string;
    onReload?: (v: any) => void;
    onChange?: (v: boolean) => void;
    class?: string;
    type?: 'default' | 'shadow' | 'button' | 'text';
  }>(),
  {
    type: 'default',
  },
);

const emit = defineEmits(['update:modelValue', 'change', 'reload']);

const displayText = computed(() => {
  if (props.disabled) {
    if (props.disabledText) {
      return props.disabledText;
    } else {
      return $t('shang-jia-hou-ji-ke-cao-zuo');
    }
  } else if (like.value) {
    return $t('qu-xiao-shou-cang');
  } else {
    return $t('shou-cang');
  }
});

const userInfo = useUserInfo();

const like = computed({
  get: () => props.modelValue,
  set: v => {
    emit('update:modelValue', v);
    emit('change', v);
  },
});

const onCancel = inject('onCancel', () => {}) as () => void;

const icon = computed(() => {
  if (props.type === 'shadow') {
    return ShadowIcon;
  }
  if (props.type === 'button') {
    return BtnIcon;
  }
  return DefaultIcon;
});

/**
 * source只给埋点使用
 */
const source = injectResourceLocation();

let target: TARGET_FROM | undefined;

/**
 * 根据后端接口定义，这里有model_type，source，resource_type三个
 * 然后根据这三个字段匹配出埋点需要的target和source
 */

if (props.params.model_type !== undefined) {
  target = getTargetForEventTracking(props.params.model_type, props.params.resource_type);
}

const trackParams: any = {
  target,
  source: source?.source,
  org_id: userInfo.value?.organization_id,
};

const onClick = async () => {
  if (props.disabled) {
    return;
  }
  const oldValue = like.value;
  like.value = !like.value;

  try {
    if (
      [COLLECTION_MAP.PIECES, COLLECTION_MAP.PIECES_TEMPLATE, COLLECTION_MAP.OPUS].includes(
        props.params.model_type as COLLECTION_MAP,
      )
    ) {
      const data = props.data as Template;
      trackParams.template_id = data.id;
      trackParams.template_title = data.title || data.name;
    }

    if (
      [COLLECTION_MAP.PIECES, COLLECTION_MAP.PIECES_TEMPLATE].includes(
        props.params.model_type as COLLECTION_MAP,
      )
    ) {
      const data = props.data as Template;
      trackParams.tags = getTagIDs(data.tag_ids).map(item => item.tag_name);
    }

    let eventName = oldValue
      ? TRACK_EVENT.YOUYAN_PLATFORM_CANCEL_COLLECT
      : TRACK_EVENT.YOUYAN_PLATFORM_COLLECT;

    if (props.params.model_type === COLLECTION_MAP.SOURCE) {
      if (props.params.resource_type === RESOURCE_TYPE.PERSON) {
        const data = props.data as Character;
        trackParams.look_name = data?.look_name;
        trackParams.name = data?.name;
        trackParams.tags = data?.tags;
        eventName = oldValue
          ? TRACK_EVENT.YOUYAN_PLATFORM_CANCEL_COLLECT_CHARACTER
          : TRACK_EVENT.YOUYAN_PLATFORM_COLLECT_CHARACTER;
      }
    }

    let res;
    if (oldValue) {
      trackEvent(eventName, trackParams);
      const { model_id, ...rest } = props.params;
      res = await cancelCollect({
        model_ids: [model_id],
        ...rest,
      });
    } else {
      trackEvent(eventName, trackParams);
      res = await addCollect(props.params);
    }
    if (!res.code) {
      const text = !oldValue
        ? h('a', { class: 'collect-link', onClick: goLink }, $t('dian-ji-cha-kan'))
        : '';
      message.success({
        content: h('div', [!oldValue ? $t('yi-shou-cang') : $t('yi-qu-xiao-shou-cang'), text]),
        duration: 2,
      });
      emit('reload', props.params.model_id);
    } else {
      like.value = oldValue;
    }
  } catch (error) {
    console.error(error);

    like.value = oldValue;
  }
};

const goLink = () => {
  onCancel?.();
  const open = useTemplateVisible();
  open.value = false;

  // 根据 model_type 和 resource_type 动态确定 tab 值
  // 映射关系参考 favorites/store.ts 中的 ResourceType 枚举
  let tab = 1; // 默认为作品
  const modelType = props.params.model_type;
  const resourceType = props.params.resource_type ?? RESOURCE_TYPE.STUDIO;

  if (modelType === COLLECTION_MAP.OPUS) {
    // 作品
    tab = 1;
  } else if (modelType === COLLECTION_MAP.PIECES || modelType === COLLECTION_MAP.PIECES_TEMPLATE) {
    // 成片/成片模板
    tab = 2;
  } else if (modelType === COLLECTION_MAP.SOURCE) {
    // 资源，需要根据 resource_type 进一步判断
    if (resourceType === RESOURCE_TYPE.PERSON) {
      tab = 3; // 人物
    } else if (resourceType === RESOURCE_TYPE.STUDIO) {
      tab = 4; // 场景
    } else if (resourceType === RESOURCE_TYPE.TIMBRE) {
      tab = 5; // 音色
    } else if (resourceType === RESOURCE_TYPE.HEAD || resourceType === RESOURCE_TYPE.END) {
      tab = 7; // 包装
    } else {
      tab = 6; // 其他素材
    }
  }

  navigateTo(`/workspace/favorites?tab=${tab}`, { external: true });
};
</script>

<style lang="scss" scoped>
.collect-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.collect-container--disabled {
  cursor: not-allowed;
}

.collect-container--clickable {
  cursor: pointer;
}

.collect-content {
  color: var(--text-1);
  font-size: 14px;
}

.collect-link {
  margin-left: 10px;
}
</style>
